import { useEffect, useState, type ReactNode } from 'react'
import type { NavigationItem } from '@unified-lk/nav'

import { BrandIcon } from '@unified-lk/brand-icons'
import type { BrandIconName } from '@unified-lk/brand-icons'
import type { AuthSession } from '../types/plans'

function navGroup(navigation: NavigationItem[], group: string) {
  return navigation.filter((item) => item.group === group)
}

function tenantDisplayName(tenant: string): string {
  return tenant === 'dhc' ? 'DHC' : 'УлыбкаPRO'
}

function getStaffIcon(key: string): BrandIconName {
  switch (key) {
    case 'dashboard': return 'doctor'
    case 'schedule': return 'booking'
    case 'patients': return 'profile'
    case 'cashier': return 'implant'
    case 'constructor': return 'plans'
    case 'plans': return 'plans'
    case 'payments': return 'implant'
    case 'communications': return 'communication'
    case 'promotions': return 'promotions'
    default: return 'tooth'
  }
}

function StaffNavIcon({ viewKey, active }: { viewKey: string; active: boolean }) {
  return (
    <BrandIcon
      name={getStaffIcon(viewKey)}
      size={20}
      className={`mb-1 ${active ? 'drop-shadow-sm' : ''}`}
    />
  )
}

export function StaffShell({
  title,
  subtitle,
  navigation,
  currentView,
  onNavigate,
  session,
  bootstrapTenant,
  onRefresh,
  onLogout,
  fullBleed,
  children,
}: {
  title?: string
  subtitle?: string
  navigation: NavigationItem[]
  currentView: string
  onNavigate: (view: string) => void
  session: AuthSession
  bootstrapTenant: string
  onRefresh?: () => void
  onLogout: () => void
  /** Убирает padding и заголовок из main — контент занимает всё доступное пространство (для конструктора и подобных full-height layout) */
  fullBleed?: boolean
  children: ReactNode
}) {
  const primary = navGroup(navigation, 'primary')
  const secondary = navGroup(navigation, 'operations')
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    setDrawerOpen(false)
  }, [currentView])

  useEffect(() => {
    if (!drawerOpen) {
      return
    }
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [drawerOpen])

  const tenantLabel = tenantDisplayName(bootstrapTenant)

  return (
    <div className={`${fullBleed ? 'h-screen flex flex-col' : 'min-h-screen'} bg-slate-50 font-sans text-slate-900`}>
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 flex h-16 items-center gap-2 border-b border-slate-200 bg-white px-3 shadow-sm sm:px-4">

        {/* Burger button (mobile/tablet) */}
        <button
          type="button"
          aria-label={drawerOpen ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={drawerOpen}
          aria-controls="staff-mobile-drawer"
          onClick={() => setDrawerOpen((value) => !value)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 lg:hidden"
        >
          {drawerOpen ? (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Left: Logo and Brand */}
        <button
          type="button"
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-3 text-left"
          title="К панели"
        >
          <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-sm">
            <BrandIcon name="doctor" size={22} />
          </div>
          <div className="hidden sm:block">
            <span className="font-bold text-base sm:text-lg text-slate-800 tracking-tight">МИС {tenantLabel}</span>
          </div>
        </button>

        {/* Center: Primary Navigation (desktop only) */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-center lg:items-center space-x-3 mx-4">
          {primary.map((item) => {
            const isActive = currentView === item.key
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onNavigate(item.key)}
                className={`group flex min-w-[80px] flex-col items-center border-b-2 px-2 py-1 transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:border-slate-200 hover:text-blue-600'
                }`}
              >
                <StaffNavIcon viewKey={item.key} active={isActive} />
                <span className="text-[11px] font-medium uppercase tracking-wider">
                  {item.shortLabel ?? item.label}
                </span>
              </button>
            )
          })}
        </div>

        {/* Spacer for mobile */}
        <div className="flex-1 lg:hidden" />

        {/* Right: Utilities */}
        <div className="flex items-center gap-2 sm:gap-4">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50"
              title="Обновить"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}

          <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-gray-200">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              {session.user.display_name.charAt(0).toUpperCase()}
            </div>
            <div className="hidden md:block text-sm">
              <div className="font-medium text-slate-700 leading-tight truncate max-w-[140px]">{session.user.display_name}</div>
              <div className="text-[11px] text-slate-500 leading-tight">Сотрудник</div>
            </div>
          </div>

          <div className="sm:hidden h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
            {session.user.display_name.charAt(0).toUpperCase()}
          </div>

          <button
            onClick={onLogout}
            className="p-1.5 text-slate-400 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
            title="Выйти"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            id="staff-mobile-drawer"
            className="absolute left-0 top-0 flex h-full w-[280px] max-w-[85vw] flex-col bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
              <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center text-white">
                <BrandIcon name="doctor" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-800">МИС {tenantLabel}</p>
                <p className="truncate text-[11px] text-slate-500">{session.user.display_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Закрыть меню"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-2 py-3">
              {primary.length > 0 && (
                <div className="mb-2">
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    Основное
                  </p>
                  <ul className="space-y-0.5">
                    {primary.map((item) => {
                      const isActive = currentView === item.key
                      return (
                        <li key={item.key}>
                          <button
                            type="button"
                            onClick={() => {
                              onNavigate(item.key)
                              setDrawerOpen(false)
                            }}
                            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <BrandIcon
                              name={getStaffIcon(item.key)}
                              size={18}
                              className={isActive ? 'text-white' : 'text-slate-500'}
                            />
                            <span className="truncate">{item.label}</span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {secondary.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
                    Операции
                  </p>
                  <ul className="space-y-0.5">
                    {secondary.map((item) => {
                      const isActive = currentView === item.key
                      return (
                        <li key={item.key}>
                          <button
                            type="button"
                            onClick={() => {
                              onNavigate(item.key)
                              setDrawerOpen(false)
                            }}
                            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-slate-800 text-white'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <BrandIcon
                              name={getStaffIcon(item.key)}
                              size={18}
                              className={isActive ? 'text-white' : 'text-slate-500'}
                            />
                            <span className="truncate">{item.label}</span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </nav>

            <div className="border-t border-slate-200 p-3">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false)
                  onLogout()
                }}
                className="flex w-full items-center justify-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Выйти
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Secondary Navigation row (desktop only) */}
      {secondary.length > 0 && (
        <div className="hidden lg:flex bg-white border-b border-gray-200 px-4 py-2 gap-2 overflow-x-auto">
          {secondary.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavigate(item.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                currentView === item.key
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {item.shortLabel ?? item.label}
            </button>
          ))}
        </div>
      )}

      {/* Main Content */}
      {fullBleed ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          {children}
        </div>
      ) : (
        <main className="px-4 pt-6 pb-8 sm:px-6 sm:pt-8 lg:px-8 lg:pt-10 max-w-[1600px] mx-auto">
          {(title || subtitle) && (
            <div className="mb-6 sm:mb-8">
              {title && <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h1>}
              {subtitle && <p className="mt-1.5 text-sm text-slate-500">{subtitle}</p>}
            </div>
          )}
          {children}
        </main>
      )}
    </div>
  )
}
